import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, Camera, Upload, Truck, Users, ShieldCheck, FileText, X, LogOut } from "lucide-react";
import { SignaturePad } from "@/components/SignaturePad";
import { Logo } from "@/components/Logo";
import { TERMS_SECTIONS, KEY_CLAUSES } from "@/data/agreementTerms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-line bg-slate-100/70 px-4 py-3 text-base text-slate-900 placeholder-zinc-400 outline-none focus:border-blaze";
const labelCls = "mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-500";

const Section = ({ title, children }) => (
  <div className="rounded-2xl border border-line bg-white p-5 shadow-sm">
    <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-blaze">{title}</p>
    {children}
  </div>
);

export const LockScreen = ({ onStart, onLogout, graceUntil }) => (
  <div className="flex min-h-screen flex-col items-center justify-center bg-slate-100 px-6" data-testid="mover-lock-screen">
    <div className="w-full max-w-sm rounded-3xl border border-line bg-white p-8 text-center shadow-xl">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
        <Lock className="h-8 w-8 text-red-500" />
      </span>
      <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Account Locked</h1>
      <p className="mt-2 text-sm text-slate-600" data-testid="lock-message">
        Complete Profile &amp; Sign Agreement to Access Jobs.
      </p>
      {graceUntil && (
        <p className="mt-2 rounded-xl bg-amber-50 p-2 text-xs font-semibold text-amber-700">
          Your temporary access expired. Please complete your onboarding to continue.
        </p>
      )}
      <button
        onClick={onStart}
        className="mt-6 w-full rounded-xl bg-blaze py-4 text-sm font-bold uppercase tracking-widest text-white active:scale-[0.98]"
        data-testid="start-onboarding-btn"
      >
        Complete Onboarding
      </button>
      <button onClick={onLogout} className="mt-3 flex w-full items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400" data-testid="lock-logout-btn">
        <LogOut className="h-3.5 w-3.5" /> Logout
      </button>
    </div>
  </div>
);

export const TermsModal = ({ onClose }) => (
  <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center" data-testid="terms-modal">
    <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-t-3xl bg-white sm:rounded-3xl">
      <div className="flex items-center justify-between border-b border-line p-4">
        <p className="font-display text-lg font-bold text-slate-900">Terms &amp; Conditions</p>
        <button onClick={onClose} className="rounded-full bg-slate-100 p-2" data-testid="terms-modal-close">
          <X className="h-4 w-4 text-slate-600" />
        </button>
      </div>
      <div className="overflow-y-auto p-5">
        <TermsBody />
      </div>
    </div>
  </div>
);

const TermsBody = () => (
  <div className="space-y-4" data-testid="terms-body">
    {TERMS_SECTIONS.map((s) => (
      <div key={s.title}>
        <p className="text-sm font-bold text-slate-900">{s.title}</p>
        <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-slate-600">{s.body}</p>
      </div>
    ))}
  </div>
);

const PhotoUpload = ({ userId, hasPhoto, onUploaded }) => {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(hasPhoto ? `${API}/onboarding/photo/${userId}?t=0` : null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/onboarding/photo`, fd);
      setPreview(URL.createObjectURL(file));
      onUploaded();
      toast.success("Profile photo uploaded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid="photo-upload">
      <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-line bg-slate-100">
        {uploading ? (
          <Loader2 className="h-6 w-6 animate-spin text-blaze" />
        ) : preview ? (
          <img src={preview} alt="Profile" className="h-full w-full object-cover" data-testid="photo-preview" />
        ) : (
          <Camera className="h-7 w-7 text-slate-400" />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <button type="button" onClick={() => camRef.current?.click()} className="flex items-center gap-2 rounded-lg bg-blaze px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-white active:scale-95" data-testid="photo-camera-btn">
          <Camera className="h-3.5 w-3.5" /> Take Photo
        </button>
        <button type="button" onClick={() => galRef.current?.click()} className="flex items-center gap-2 rounded-lg border border-blaze px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-blaze active:scale-95" data-testid="photo-gallery-btn">
          <Upload className="h-3.5 w-3.5" /> Upload from Gallery
        </button>
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="photo-camera-input" />
      <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="photo-gallery-input" />
    </div>
  );
};

export default function Onboarding({ user, status, onComplete, onLogout }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState(null);
  const [hasPhoto, setHasPhoto] = useState(status.has_photo);
  const p = status.profile || {};
  const [form, setForm] = useState({
    worker_role: status.worker_role || "",
    full_name: p.full_name || status.prefill.full_name || "",
    phone: p.phone || status.prefill.phone || "",
    address: p.address || status.prefill.address || "",
    licence_number: p.licence_number || "",
    licence_expiry: p.licence_expiry || "",
    licence_state: p.licence_state || "VIC",
    licence_class: p.licence_class || "",
    demerit_history: p.demerit_history || "",
    emergency_name: p.emergency_name || "",
    emergency_relationship: p.emergency_relationship || "",
    emergency_phone: p.emergency_phone || "",
    bank_name: p.bank_name || "",
    account_holder: p.account_holder || "",
    bsb: p.bsb || "",
    account_number: p.account_number || "",
    abn: p.abn || "",
    superannuation: p.superannuation || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const today = new Date().toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" });
  const isDriver = form.worker_role === "driver";

  const saveProfile = async () => {
    if (!form.worker_role) return toast.error("Select your role — Driver or Helper.");
    if (!form.full_name.trim() || !form.phone.trim() || !form.address.trim()) return toast.error("Full name, phone and address are required.");
    if (isDriver && (!form.licence_number.trim() || !form.licence_expiry)) return toast.error("Licence number and expiry are required for drivers.");
    if (!form.emergency_name.trim() || !form.emergency_phone.trim() || !form.emergency_relationship.trim()) return toast.error("Emergency contact details are required.");
    if (!form.bank_name.trim() || !form.account_holder.trim() || !form.bsb.trim() || !form.account_number.trim() || !form.abn.trim()) return toast.error("Bank details and ABN are required.");
    setSaving(true);
    try {
      await axios.post(`${API}/onboarding/profile`, form);
      setStep(2);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  const submitAgreement = async () => {
    if (!accepted) return toast.error("Please tick the checkbox to accept the Terms and Conditions.");
    if (!signature) return toast.error("Please sign in the signature box.");
    setSaving(true);
    try {
      await axios.post(`${API}/onboarding/agreement`, { accepted: true, signature, signed_date: today });
      toast.success("Agreement signed & submitted — welcome aboard!");
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit agreement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 pb-16" data-testid="onboarding-flow">
      <header className="sticky top-0 z-40 border-b border-line bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-2xl items-center justify-between px-4">
          <Logo testid="onboarding-logo" iconSize="h-7" textSize="text-sm" />
          <span className="rounded-full bg-blue-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-blaze" data-testid="onboarding-step-badge">
            Step {step} of 2
          </span>
        </div>
        <div className="h-1 bg-slate-200"><div className="h-1 bg-blaze transition-all" style={{ width: step === 1 ? "50%" : "100%" }} /></div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 pt-5">
        {step === 1 && (
          <div className="space-y-4" data-testid="onboarding-profile-step">
            <div>
              <h1 className="font-display text-2xl font-bold text-slate-900">Your <span className="text-blaze">Profile</span></h1>
              <p className="mt-1 text-sm text-slate-500">Complete your details to unlock your jobs dashboard.</p>
            </div>

            <Section title="Your Role">
              <div className="grid grid-cols-2 gap-3" data-testid="role-toggle">
                {[["driver", "Driver", Truck], ["helper", "Helper / Offsider", Users]].map(([k, l, Icon]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => set("worker_role", k)}
                    className={`flex flex-col items-center gap-2 rounded-2xl border-2 p-4 text-sm font-bold transition-colors active:scale-[0.98] ${form.worker_role === k ? "border-blaze bg-blue-50 text-blaze" : "border-line bg-white text-slate-600"}`}
                    data-testid={`role-${k}`}
                  >
                    <Icon className="h-6 w-6" /> {l}
                  </button>
                ))}
              </div>
            </Section>

            <Section title="Profile Picture">
              <PhotoUpload userId={user.id} hasPhoto={hasPhoto} onUploaded={() => setHasPhoto(true)} />
            </Section>

            <Section title="Personal Details">
              <div className="grid gap-3 sm:grid-cols-2">
                <div><label className={labelCls}>Full Name *</label><input className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="ob-full-name" /></div>
                <div><label className={labelCls}>Email (from account)</label><input className={`${inputCls} opacity-60`} value={status.prefill.email} readOnly data-testid="ob-email" /></div>
                <div><label className={labelCls}>Phone Number *</label><input type="tel" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="04xx xxx xxx" data-testid="ob-phone" /></div>
                <div><label className={labelCls}>Home Address *</label><input className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Street, Suburb, State" data-testid="ob-address" /></div>
              </div>
            </Section>

            {isDriver && (
              <Section title="Driver Licence (Drivers Only)">
                <div className="grid gap-3 sm:grid-cols-2" data-testid="licence-fields">
                  <div><label className={labelCls}>Licence Number *</label><input className={inputCls} value={form.licence_number} onChange={(e) => set("licence_number", e.target.value)} data-testid="ob-licence-number" /></div>
                  <div><label className={labelCls}>Expiry Date *</label><input type="date" className={inputCls} value={form.licence_expiry} onChange={(e) => set("licence_expiry", e.target.value)} data-testid="ob-licence-expiry" /></div>
                  <div>
                    <label className={labelCls}>State</label>
                    <select className={inputCls} value={form.licence_state} onChange={(e) => set("licence_state", e.target.value)} data-testid="ob-licence-state">
                      {["VIC", "NSW", "QLD", "SA", "WA", "TAS", "NT", "ACT"].map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div><label className={labelCls}>Licence Class</label><input className={inputCls} value={form.licence_class} onChange={(e) => set("licence_class", e.target.value)} placeholder="e.g. C, LR, MR" data-testid="ob-licence-class" /></div>
                  <div className="sm:col-span-2"><label className={labelCls}>Demerit Point History</label><textarea rows={2} className={inputCls} value={form.demerit_history} onChange={(e) => set("demerit_history", e.target.value)} placeholder="e.g. 2 points — speeding 2024, or 'None'" data-testid="ob-demerits" /></div>
                </div>
              </Section>
            )}

            <Section title="Emergency Contact">
              <div className="grid gap-3 sm:grid-cols-3">
                <div><label className={labelCls}>Contact Name *</label><input className={inputCls} value={form.emergency_name} onChange={(e) => set("emergency_name", e.target.value)} data-testid="ob-emergency-name" /></div>
                <div><label className={labelCls}>Relationship *</label><input className={inputCls} value={form.emergency_relationship} onChange={(e) => set("emergency_relationship", e.target.value)} placeholder="e.g. Spouse, Parent" data-testid="ob-emergency-rel" /></div>
                <div><label className={labelCls}>Phone Number *</label><input type="tel" className={inputCls} value={form.emergency_phone} onChange={(e) => set("emergency_phone", e.target.value)} data-testid="ob-emergency-phone" /></div>
              </div>
            </Section>

            <Section title="Payroll Details">
              <div className="grid gap-3 sm:grid-cols-2">
                <div><label className={labelCls}>Bank Name *</label><input className={inputCls} value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="ob-bank-name" /></div>
                <div><label className={labelCls}>Account Holder Name *</label><input className={inputCls} value={form.account_holder} onChange={(e) => set("account_holder", e.target.value)} data-testid="ob-account-holder" /></div>
                <div><label className={labelCls}>BSB *</label><input inputMode="numeric" className={inputCls} value={form.bsb} onChange={(e) => set("bsb", e.target.value)} placeholder="000-000" data-testid="ob-bsb" /></div>
                <div><label className={labelCls}>Account Number *</label><input inputMode="numeric" className={inputCls} value={form.account_number} onChange={(e) => set("account_number", e.target.value)} data-testid="ob-account-number" /></div>
                <div><label className={labelCls}>ABN *</label><input inputMode="numeric" className={inputCls} value={form.abn} onChange={(e) => set("abn", e.target.value)} placeholder="11 digit ABN" data-testid="ob-abn" /></div>
                <div><label className={labelCls}>Superannuation (optional)</label><input className={inputCls} value={form.superannuation} onChange={(e) => set("superannuation", e.target.value)} placeholder="Fund name / member no." data-testid="ob-super" /></div>
              </div>
            </Section>

            <button
              onClick={saveProfile}
              disabled={saving}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-blaze py-4 text-sm font-bold uppercase tracking-widest text-white active:scale-[0.98] disabled:opacity-50"
              data-testid="ob-continue-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Continue to Terms &amp; Conditions
            </button>
            <button onClick={onLogout} className="flex w-full items-center justify-center gap-2 py-2 text-xs font-bold uppercase tracking-widest text-slate-400" data-testid="ob-logout-btn">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="onboarding-terms-step">
            <div>
              <h1 className="font-display text-2xl font-bold text-slate-900">Terms &amp; <span className="text-blaze">Conditions</span></h1>
              <p className="mt-1 text-sm text-slate-500">Read and sign the contractor agreement to activate your account.</p>
            </div>

            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4" data-testid="key-clauses">
              <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-blaze"><ShieldCheck className="h-4 w-4" /> Key Clauses</p>
              <ul className="space-y-1 text-[13px] text-slate-700">
                {KEY_CLAUSES.map((c) => <li key={c}>• {c}</li>)}
              </ul>
            </div>

            <Section title="Contractor Agreement">
              <div className="max-h-80 overflow-y-auto rounded-xl border border-line bg-slate-50 p-4" data-testid="terms-scroll">
                <TermsBody />
              </div>
              <button type="button" onClick={() => setShowTerms(true)} className="mt-3 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-blaze" data-testid="read-full-terms-btn">
                <FileText className="h-4 w-4" /> Read Full Terms
              </button>
            </Section>

            <Section title="Acceptance & Digital Signature">
              <label className="flex cursor-pointer items-start gap-3" data-testid="accept-terms-label">
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 h-5 w-5 accent-blue-600" data-testid="accept-terms-checkbox" />
                <span className="text-sm font-semibold text-slate-800">I accept all Terms and Conditions of the MoversNearby contractor agreement.</span>
              </label>
              <div className="mt-4">
                <SignaturePad label="Your Signature" onChange={setSignature} testid="ob-signature" />
              </div>
              <div className="mt-3 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm">
                <span className="text-slate-500">Date</span>
                <span className="font-bold text-slate-900" data-testid="ob-date">{today}</span>
              </div>
            </Section>

            <button
              onClick={submitAgreement}
              disabled={saving}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-blaze py-4 text-sm font-bold uppercase tracking-widest text-white active:scale-[0.98] disabled:opacity-50"
              data-testid="ob-submit-agreement-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sign &amp; Submit Agreement
            </button>
            <button onClick={() => setStep(1)} className="w-full py-2 text-xs font-bold uppercase tracking-widest text-slate-400" data-testid="ob-back-btn">
              Back to Profile
            </button>
          </div>
        )}
      </main>

      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
    </div>
  );
}
